import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup";
import {
  PermissionModel,
  PermissionCategoryModel,
  PermissionManagementMatrixModel,
  UserPermissionModel,
  UserModel,
} from "services/api/cplauthapi/CPLAuthAPI";
import { container } from "setup/dependency/inversify.config";
import { IPermissionsService } from "services/PermissionsService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";
import { IUsersService } from "services/UsersService";

const initialState = {
  IsSuperUser: false,
  Permissions: new Array<PermissionModel>(),
  PermissionCategories: new Array<PermissionCategoryModel>(),
  PermissionAssignments: new PermissionManagementMatrixModel(),
  UserPermissions: new Array<UserPermissionModel>(),
};

export const usersPermissionsSlice = createSlice({
  name: "userPermissions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.UserPermissions = action.payload;
      })
      .addCase(fetchAllPermissionCategories.fulfilled, (state, action) => {
        state.PermissionCategories = action.payload;
      })
      .addCase(fetchAllPermissionsForCategory.fulfilled, (state, action) => {
        state.Permissions = action.payload;
      })
      .addCase(grantPermissionAssignment.fulfilled, () => {
        toast.success("Permission Granted");
      })
      .addCase(denyPermissionAssignment.fulfilled, () => {
        toast.success("Permission Denied");
      })
      .addCase(deletePermissionAssignment.fulfilled, () => {
        toast.success("Permission Deleted");
      })
      .addCase(isSuperUser.fulfilled, (state, action) => {
        state.IsSuperUser = action.payload;
      });
  },
});

export const fetchUserPermissions = createAsyncThunk("UserPermissions/FetchUserPermissions", async (userID: string) => {
  const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
  return await permissionsService.getAllPermissionsForUser(userID);
});

export const fetchAllPermissionCategories = createAsyncThunk(
  "UserPermissions/FetchAllPermissionCategories",
  async () => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    return await permissionsService.getAllPermissionCategories();
  }
);

export const fetchAllPermissionsForCategory = createAsyncThunk(
  "UserPermissions/FetchAllPermissionsForCategory",
  async (permissionCategoryID: number) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    return await permissionsService.getAllPermissionsForCategory(permissionCategoryID);
  }
);

export const grantPermissionAssignment = createAsyncThunk(
  "UserPermissions/GrantPermissionAssignment",
  async ({ userID, permissionID }: { userID: string; permissionID: number }, thunkAPI) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    const response = await permissionsService.grantUserPermissionAssignment(userID, permissionID);
    thunkAPI.dispatch(fetchUserPermissions(userID));
    return response;
  }
);

export const denyPermissionAssignment = createAsyncThunk(
  "UserPermissions/DenyPermissionAssignment",
  async ({ userID, permissionID }: { userID: string; permissionID: number }, thunkAPI) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    const response = await permissionsService.denyUserPermissionAssignment(userID, permissionID);
    thunkAPI.dispatch(fetchUserPermissions(userID));
    return response;
  }
);

export const deletePermissionAssignment = createAsyncThunk(
  "UserPermissions/DeletePermissionAssignment",
  async ({ userID, permissionAssignmentID }: { userID: string; permissionAssignmentID: number }, thunkAPI) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    const response = await permissionsService.deletePermissionAssignment(permissionAssignmentID);
    thunkAPI.dispatch(fetchUserPermissions(userID));
    return response;
  }
);

export const isSuperUser = createAsyncThunk<boolean, { userID?: string }, { state: RootState }>(
  "UserPermissions/isSuperUser",
  async ({ userID }: { userID?: string }, thunkAPI) => {
    const state = thunkAPI.getState();
    const currentUser: UserModel = state.auth.user!;
    if (userID === null || userID === undefined || userID === currentUser?.userID) {
      return currentUser?.superUser ?? false;
    }
    const usersService = container.get<IUsersService>(SERVICE_TYPES.UsersService);
    const response = await usersService.getUserProfile(userID);
    return response.superUser ?? false;
  }
);

export const selectPermissions = (state: RootState) => {
  return state.userPermissions.Permissions;
};
export const selectUserPermissions = (state: RootState) => {
  return state.userPermissions.UserPermissions;
};
export const selectPermissionCategories = (state: RootState) => {
  return state.userPermissions.PermissionCategories;
};
export const selectIsSuperUser = (state: RootState) => {
  return state.userPermissions.IsSuperUser;
};

export default usersPermissionsSlice.reducer;
